<template>
  <g ref="group"> </g>
</template>

<script lang="ts" setup>
import * as d3 from 'd3'
import { ref, toRefs, watch, onUnmounted, computed } from 'vue'
import { useComponent } from './useComponent'
import { ColorScaleInterpolate, ColorScaleStep, configToSvgGradient } from '@/utils/colorScale'
import { Line } from '../chart/lines'
import { PatternType } from '../chart/bars'
import { addPattern } from '../utils'
import { throttle } from 'lodash-es'

export type LineProps = {
  values: Line
  stroke?: string
  fill?: string
  pattern?: PatternType
  label?: string
  strokeWidth?: number
  dashArray?: number[]
  gradient?: {
    fill?: ColorScaleInterpolate | ColorScaleStep
    stroke?: ColorScaleInterpolate | ColorScaleStep
  }
  xLimits: [number, number]
  yLimits: [number, number]
  highlightStrokeWidthMultiplier?: number
  highlightFadedOpacity?: number
  transitionDuration?: number
  highlightable?: boolean
}

const props = withDefaults(defineProps<LineProps>(), {
  stroke: 'none',
  fill: 'none',
  pattern: undefined,
  label: undefined,
  strokeWidth: 2,
  dashArray: undefined,
  gradient: undefined,
  highlightStrokeWidthMultiplier: 2,
  highlightFadedOpacity: 0.3,
  transitionDuration: 150,
  highlightable: false
})

const {
  values,
  stroke,
  xLimits,
  yLimits,
  strokeWidth,
  fill,
  dashArray,
  gradient,
  pattern,
  highlightStrokeWidthMultiplier,
  highlightFadedOpacity,
  transitionDuration,
  highlightable
} = toRefs(props)

const group = ref<SVGGElement | null>(null)
let baseLinesSelection: d3.Selection<
  SVGPathElement,
  [number, number][],
  SVGGElement,
  unknown
> | null = null
const highlightStrokeWidth = computed(
  () => (strokeWidth?.value ?? 2) * highlightStrokeWidthMultiplier.value
)
const defaultStrokeWidth = computed(() => strokeWidth?.value ?? 2)
const fadedOpacity = computed(() => highlightFadedOpacity.value)

const splitLineByNull = (line: Line): [number, number][][] => {
  const splitLines: [number, number][][] = []
  let currentLine: [number, number][] = []
  for (const point of line) {
    if (!Array.isArray(point) || point.length !== 2) {
      continue
    }
    const [x, y] = point
    if (y === null) {
      if (currentLine.length > 0) {
        splitLines.push(currentLine)
      }
      currentLine = []
    } else if (typeof x === 'number' && typeof y === 'number') {
      currentLine.push([x, y])
    } else {
      if (currentLine.length > 0) {
        splitLines.push(currentLine)
      }
      currentLine = []
    }
  }
  if (currentLine.length > 0) {
    splitLines.push(currentLine)
  }
  return splitLines.filter((lineSegment) => lineSegment.length >= 2)
}

const doesLineContainPoint = (
  lineData: [number, number][],
  targetX: number,
  targetY: number,
  tolerance: number = 1e-9
): boolean => {
  if (!Array.isArray(lineData)) return false
  for (const [px, py] of lineData) {
    if (Math.abs(px - targetX) < tolerance && Math.abs(py - targetY) < tolerance) {
      return true
    }
  }
  return false
}

let handleMouseMoveThrottled: ((event: MouseEvent) => void) | null = null
let handleMouseLeave: (() => void) | null = null
let currentSvgNode: SVGElement | null = null

useComponent(group, ({ width, height, group: g, svg, parentGroup }) => {
  const defs = svg.select<SVGDefsElement>('defs')
  let patternId: string | undefined
  if (pattern?.value && fill?.value) {
    patternId = addPattern(
      { type: pattern.value, color: fill.value, strokeWidth: strokeWidth?.value },
      defs as unknown as d3.Selection<d3.BaseType, unknown, null, undefined>
    )
  }

  const xScale = d3.scaleLinear().range([0, width])
  const yScale = d3.scaleLinear().range([height, 0])
  const lineGenerator = d3
    .line<[number, number]>()
    .x((d) => xScale(d[0]))
    .y((d) => yScale(d[1]))
  const baseLinesGroup = g.append('g').attr('class', 'base-lines')

  const resetHighlightState = () => {
    if (!highlightable.value || !baseLinesSelection) return

    baseLinesSelection.each(function () {
      d3.select(this)
        .transition()
        .duration(transitionDuration.value)
        .attr('stroke-width', defaultStrokeWidth.value)
        .style('opacity', 1)
    })
  }

  const drawBaseLines = () => {
    const lines = splitLineByNull(values.value)
    baseLinesSelection = baseLinesGroup
      .selectAll<SVGPathElement, [number, number][]>('path.base-line')
      .data(lines, (d, i) => i)
      .join(
        (enter) =>
          enter
            .append('path')
            .attr('class', 'base-line')
            .attr('stroke-width', defaultStrokeWidth.value)
            .attr('stroke-dasharray', dashArray?.value?.join(' ') ?? 'none')
            .attr('stroke-linecap', 'round')
            .attr('stroke-linejoin', 'round')
            .style('opacity', 1)
            .style('pointer-events', 'none'),
        (update) =>
          update
            .attr('stroke-width', defaultStrokeWidth.value)
            .attr('stroke-dasharray', dashArray?.value?.join(' ') ?? 'none')
            .style('opacity', 1),
        (exit) => exit.remove()
      )
      .each(function (points) {
        const [fillGradientId] = gradient?.value?.fill
          ? addGradient(points, gradient.value.fill, defs)
          : [undefined]
        const [strokeGradientId] = gradient?.value?.stroke
          ? addGradient(points, gradient.value.stroke, defs)
          : [undefined]
        const fillValue = patternId
          ? `url(#${patternId})`
          : fillGradientId
          ? `url(#${fillGradientId})`
          : fill?.value && fill.value !== 'none'
          ? fill.value
          : 'none'
        const strokeValue = strokeGradientId ? `url(#${strokeGradientId})` : stroke?.value ?? 'none'

        d3.select(this)
          .attr('fill', fillValue)
          .attr('stroke', strokeValue)
          .attr('d', lineGenerator(points))
      })

    if (highlightable.value) {
      resetHighlightState()
    } else {
      baseLinesSelection?.attr('stroke-width', defaultStrokeWidth.value).style('opacity', 1)
    }
  }

  const updateHighlightState = () => {
    if (!highlightable.value || !parentGroup?.node() || !baseLinesSelection) return

    const highlightedElements = parentGroup.selectAll('.highlight').nodes() as Element[]
    const targetCoords = highlightedElements
      .map((el) => {
        const x = parseFloat(el.getAttribute('data-tooltip-x') || 'NaN')
        const y = parseFloat(el.getAttribute('data-tooltip-y') || 'NaN')
        return { x, y }
      })
      .filter((coord) => !isNaN(coord.x) && !isNaN(coord.y))

    const isAnyHighlightPresent = targetCoords.length > 0
    let isSelfHighlighted = false

    if (isAnyHighlightPresent) {
      baseLinesSelection.each(function (lineData) {
        if (isSelfHighlighted) return
        for (const coord of targetCoords) {
          if (doesLineContainPoint(lineData, coord.x, coord.y)) {
            isSelfHighlighted = true
            return
          }
        }
      })
    }

    baseLinesSelection.each(function () {
      let targetOpacity = 1
      let targetStrokeWidth = defaultStrokeWidth.value

      if (isAnyHighlightPresent) {
        if (isSelfHighlighted) {
          targetOpacity = 1
          targetStrokeWidth = highlightStrokeWidth.value
        } else {
          targetOpacity = fadedOpacity.value
          targetStrokeWidth = defaultStrokeWidth.value
        }
      }

      d3.select(this)
        .transition()
        .duration(transitionDuration.value)
        .attr('stroke-width', targetStrokeWidth)
        .style('opacity', targetOpacity)
    })
  }

  const updateScalesAndRedraw = () => {
    xScale.domain(xLimits.value!).range([0, width])
    yScale.domain(yLimits.value!).range([height, 0])
    drawBaseLines()
  }

  watch([xLimits, yLimits, () => width, () => height, values], updateScalesAndRedraw, {
    deep: true,
    immediate: true
  })
  watch([defaultStrokeWidth, fadedOpacity, transitionDuration, highlightable], () => {
    drawBaseLines()
  })

  const setupEventListeners = () => {
    removeEventListeners()

    if (highlightable.value && currentSvgNode) {
      const throttledUpdate = throttle(updateHighlightState, 50)
      handleMouseMoveThrottled = throttledUpdate
      handleMouseLeave = resetHighlightState

      const eventTarget = parentGroup?.node() ?? currentSvgNode

      eventTarget.addEventListener('mousemove', throttledUpdate)
      eventTarget.addEventListener('mouseleave', resetHighlightState)
    }
  }

  const removeEventListeners = () => {
    const eventTarget = parentGroup?.node() ?? currentSvgNode
    if (eventTarget) {
      if (handleMouseMoveThrottled) {
        eventTarget.removeEventListener('mousemove', handleMouseMoveThrottled)
      }
      if (handleMouseLeave) {
        eventTarget.removeEventListener('mouseleave', handleMouseLeave)
      }
    }

    handleMouseMoveThrottled = null
    handleMouseLeave = null
  }

  currentSvgNode = svg.node()
  watch(highlightable, setupEventListeners, { immediate: true })

  onUnmounted(() => {
    removeEventListeners()
    currentSvgNode = null
    baseLinesSelection = null
  })
})

function addGradient(
  values: [number, number][],
  config: ColorScaleInterpolate | ColorScaleStep,
  defs: d3.Selection<SVGDefsElement, unknown, null, undefined>
): [string, boolean] {
  const gradientData = configToSvgGradient(
    config as any,
    config.orientation === 'horizontal' ? values.map((d) => d[0]) : values.map((d) => d[1])
  )

  if (!gradientData) {
    console.warn('Failed to generate gradient data from config:', config)
    return ['', false]
  }

  const gradientDef = defs.select(`#${gradientData.id}`)

  if (gradientDef.empty()) {
    defs
      .append(gradientData.type)
      .attr('id', gradientData.id)
      .attr('x1', gradientData.p1[0])
      .attr('y1', gradientData.p1[1])
      .attr('x2', gradientData.p2[0])
      .attr('y2', gradientData.p2[1])
      .selectAll('stop')
      .data(gradientData.stops)
      .join('stop')
      .attr('offset', (d) => `${Math.floor(d.offset * 100)}%`)
      .attr('stop-opacity', (d) => (d.opacity !== undefined ? d.opacity : 1))
      .attr('stop-color', (d) => d.color)

    return [gradientData.id, true]
  }

  return [gradientData.id, true]
}
</script>

<style scoped>
.base-line {
  transition-property: stroke-width, opacity;
  transition-duration: v-bind('`${transitionDuration}ms`');
  transition-timing-function: linear;
}
</style>
